import { useCallback, useEffect, useState } from "react";
import { ExternalLink, FolderSync, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/constants";
import { toast } from "sonner";

function CountCard({ label, value }) {
  return <div className="border border-pleiades-border bg-white p-3"><div className="text-[10px] uppercase tracking-wider text-pleiades-muted">{label}</div><div className="text-xl font-semibold mt-1">{value ?? 0}</div></div>;
}

export default function ClientDossierPanel({ clientId }) {
  const [dossier, setDossier] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/clients/${clientId}/dossier`);
    setDossier(data);
  }, [clientId]);

  useEffect(() => { load().catch((error) => toast.error(formatApiError(error))); }, [load]);

  const preparePreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/clients/${clientId}/drive-dossier/preview`);
      setPreview(data);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setBusy(false); }
  };

  const sync = async () => {
    if (!preview || !window.confirm("Önizlemedeki tablolar Google Drive danışan dosyasına yazılsın mı?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/clients/${clientId}/drive-dossier/sync`, { preview_id: preview.preview_id });
      setPreview(null);
      await load();
      toast.success("Google Drive danışan dosyası güncellendi");
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setBusy(false); }
  };

  if (!dossier) return <div className="text-[12px] text-pleiades-slate py-10">Danışan dosyası hazırlanıyor…</div>;
  const summary = dossier.summary || {};
  const drive = dossier.google_drive || {};
  const mapping = drive.mapping || {};

  return <div className="space-y-4" data-testid="client-dossier-panel">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <CountCard label="Anket" value={summary.questionnaire_count} />
      <CountCard label="Çalışma" value={summary.work_count} />
      <CountCard label="Tamamlanan" value={summary.completed_work_count} />
      <CountCard label="Deneyim" value={summary.experience_count} />
      <CountCard label="Site hareketi" value={summary.activity_count} />
      <CountCard label="Randevu" value={summary.appointment_count} />
      <CountCard label="Mesaj" value={summary.message_count} />
    </div>

    <section className="border border-pleiades-border bg-white p-4">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div><h3 className="text-[13px] font-semibold flex items-center gap-2"><FolderSync className="w-4 h-4" /> Google Drive danışan dosyası</h3>
          <p className="text-[11px] text-pleiades-muted mt-1">Portal veritabanı asıl kayıttır. Drive dosyası yalnızca yönetim amaçlı, onaylı bir yansımadır.</p>
          {mapping.last_synced_at && <p className="text-[11px] text-pleiades-slate mt-2">Son eşitleme: {formatDateTime(mapping.last_synced_at)}</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          {mapping.folder_url && <a href={mapping.folder_url} target="_blank" rel="noreferrer" className="min-h-9 px-3 border border-pleiades-border text-[12px] inline-flex items-center gap-1.5">Drive’da aç <ExternalLink className="w-3.5 h-3.5" /></a>}
          <button disabled={busy} onClick={preparePreview} className="min-h-9 px-3 border border-pleiades-border text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50"><RefreshCw className="w-3.5 h-3.5" /> Önizleme hazırla</button>
        </div>
      </div>
      {!drive.configured && <div className="mt-3 border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-900">Google Drive bağlantısı henüz yapılandırılmadı. Sistem dışarıya veri yazmaz; bağlantı kurulunca önizleme ve açık onayla eşitleme açılır.</div>}
      {preview && <div className="mt-4 border border-pleiades-border bg-pleiades-ivory p-3">
        <div className="text-[12px] font-medium">Yazılacak klasör: {preview.folder_name}</div>
        <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-4 gap-2">{preview.tables.map((table) => <div key={table.title} className="bg-white border border-pleiades-border px-3 py-2 text-[11px]"><span className="font-medium">{table.title}</span><span className="text-pleiades-muted"> · {table.row_count} kayıt</span></div>)}</div>
        <button disabled={!preview.google_drive?.configured || busy} onClick={sync} className="mt-3 min-h-10 px-4 bg-pleiades-blue text-white text-[12px] disabled:opacity-40">Drive dosyasını oluştur / güncelle</button>
      </div>}
    </section>

    <section className="border border-pleiades-border bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-pleiades-border text-[12px] font-semibold">Site dolaşımı ve eğilimler</div>
      <table className="w-full text-[12px]"><thead className="bg-pleiades-sidebar/60 text-left"><tr><th className="px-4 py-2 font-medium">Sayfa / olay</th><th className="px-4 py-2 font-medium text-right">Sayı</th></tr></thead><tbody>{dossier.route_trends?.slice(0, 20).map((item) => <tr key={item.route} className="border-t border-pleiades-border"><td className="px-4 py-2">{item.route}</td><td className="px-4 py-2 text-right tabular-nums">{item.count}</td></tr>)}</tbody></table>
    </section>
  </div>;
}
